export interface Capability {
  slug: string
  name: string
  shortDescription: string
  description: string
  howItWorks: { step: number; title: string; description: string }[]
  typicalOutputs: string[]
  supportedSectors: string[]
  icon: string
}

export const capabilities: Capability[] = [
  {
    slug: 'research-design-development',
    name: 'Research, Design and Development',
    shortDescription:
      'Concept-to-prototype services for bespoke equipment requirements.',
    description:
      'Our R&D team works collaboratively with clients to transform operational requirements into manufacturable products. We utilise CAD modelling, material science expertise, and rapid prototyping to develop solutions that meet specific performance criteria. This phase is critical for ensuring the final product is fit for purpose, cost-effective, and compliant with relevant standards.',
    howItWorks: [
      {
        step: 1,
        title: 'Requirement Analysis',
        description:
          'We review your technical specifications and operational needs.',
      },
      {
        step: 2,
        title: 'Concept Design',
        description: 'Initial sketches and CAD models are created for review.',
      },
      {
        step: 3,
        title: 'Material Selection',
        description:
          'We identify appropriate materials based on performance and cost.',
      },
      {
        step: 4,
        title: 'Prototyping',
        description:
          'Physical samples are produced for evaluation and testing.',
      },
      {
        step: 5,
        title: 'Design Freeze',
        description: 'Final specifications are agreed upon for production.',
      },
    ],
    typicalOutputs: [
      'Technical drawings',
      '3D CAD models',
      'Material samples',
      'Functional prototypes',
      'Product specifications',
    ],
    supportedSectors: ['defence', 'police', 'medical', 'prisons'],
    icon: 'PenTool',
  },
  {
    slug: 'manufacturing-production',
    name: 'Manufacturing and Production',
    shortDescription:
      'Scalable UK and international manufacturing capabilities.',
    description:
      'Chiltern Global operates flexible manufacturing lines capable of handling both low-volume specialist runs and high-volume mass production. We manage a network of owned and partner facilities, ensuring we can scale production to meet demand while maintaining strict quality control. Our capabilities cover textile conversion, injection moulding, and assembly.',
    howItWorks: [
      {
        step: 1,
        title: 'Production Planning',
        description:
          'Scheduling and resource allocation based on order volume.',
      },
      {
        step: 2,
        title: 'Material Procurement',
        description: 'Sourcing raw materials from approved suppliers.',
      },
      {
        step: 3,
        title: 'Cutting & Preparation',
        description: 'Precision cutting of fabrics and components.',
      },
      {
        step: 4,
        title: 'Assembly',
        description:
          'Skilled assembly using industrial sewing and joining techniques.',
      },
      {
        step: 5,
        title: 'Finishing',
        description: 'Final trimming, inspection, and packing.',
      },
    ],
    typicalOutputs: [
      'Finished goods',
      'Sub-assemblies',
      'Component kits',
      'Private label products',
      'Bulk production runs',
    ],
    supportedSectors: ['defence', 'police', 'prisons', 'medical'],
    icon: 'Factory',
  },
  {
    slug: 'quality-assurance',
    name: 'Quality and Assurance',
    shortDescription:
      'Rigorous testing and inspection to international standards.',
    description:
      'Quality is embedded in our manufacturing process. We operate a Quality Management System aligned with ISO 9001:2015. Our QA team conducts incoming material inspections, in-process checks, and final product audits to ensure every item meets the agreed specification. We also facilitate third-party testing for certification purposes.',
    howItWorks: [
      {
        step: 1,
        title: 'Incoming Inspection',
        description: 'Raw materials are checked against technical data sheets.',
      },
      {
        step: 2,
        title: 'In-Process Checks',
        description:
          'Regular inspections during manufacturing to catch issues early.',
      },
      {
        step: 3,
        title: 'Performance Testing',
        description:
          'Batch testing for key performance indicators (e.g., tear strength).',
      },
      {
        step: 4,
        title: 'Final Audit',
        description: 'Pre-shipment inspection of finished goods and packaging.',
      },
      {
        step: 5,
        title: 'Documentation',
        description:
          'Generation of Certificates of Conformity and inspection reports.',
      },
    ],
    typicalOutputs: [
      'Inspection reports',
      'Certificates of Conformity',
      'Test data',
      'Compliance documentation',
      'Audit trails',
    ],
    supportedSectors: ['defence', 'police', 'medical'],
    icon: 'ShieldCheck',
  },
  {
    slug: 'logistics-delivery',
    name: 'Logistics and Delivery',
    shortDescription: 'Secure supply chain management and global distribution.',
    description:
      'We provide end-to-end logistics solutions, managing the movement of goods from our manufacturing facilities to your delivery point. Our team handles customs clearance, freight forwarding, and warehousing. We offer secure storage and just-in-time delivery options to support your inventory management requirements.',
    howItWorks: [
      {
        step: 1,
        title: 'Freight Management',
        description: 'Coordination of sea, air, and road freight.',
      },
      {
        step: 2,
        title: 'Customs Clearance',
        description: 'Handling import/export documentation and duties.',
      },
      {
        step: 3,
        title: 'Warehousing',
        description: 'Secure storage of stock in our UK facilities.',
      },
      {
        step: 4,
        title: 'Order Fulfilment',
        description: 'Pick, pack, and despatch of orders.',
      },
      {
        step: 5,
        title: 'Delivery Tracking',
        description: 'Real-time updates on shipment status.',
      },
    ],
    typicalOutputs: [
      'Delivered goods',
      'Shipping documentation',
      'Inventory reports',
      'Customs declarations',
      'Proof of delivery',
    ],
    supportedSectors: ['defence', 'police', 'prisons', 'medical'],
    icon: 'Truck',
  },
]
